import { useSelector } from "react-redux";

import { useParams } from "react-router-dom";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import { Stack, Tooltip, Typography, Zoom, Box, Button, Divider } from "@mui/material";

import { CarFilled } from "@ant-design/icons";
import moment from "moment";

import Container from "Components/UI/Container";

import theme from "Shared/styles/theme";

import { PILLS_CONFIG, renderPill } from "./pillsConfig";
import VehicleActionList from "./VehicleActionList";

export default function VehicleInfo({ hideActions, sx = {} }) {
  const { leadId } = useParams();
  const {
    vehicleInfo = {},
    config = {},
    refurbEstimateDate: summaryRefurbEstimateDate
  } = useSelector((state) => state.vehicleSummary.data);

  const {
    ctas = [],
    title,
    subtitle,
    appointmentId,
    stockId,
    infoList = [],
    state,
    vin,
    properties,
    odometer,
    websiteLink,
    inventoryLocation,
    refurbEstimateDate: vehicleInfoRefurbEstimateDate
  } = vehicleInfo;
  const refurbEstimateDate = vehicleInfoRefurbEstimateDate || summaryRefurbEstimateDate;
  const parsedRefurbEstimateDate = refurbEstimateDate
    ? moment(refurbEstimateDate, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true)
    : null;
  const formattedRefurbEstimateDate =
    parsedRefurbEstimateDate && parsedRefurbEstimateDate.isValid()
      ? parsedRefurbEstimateDate.format("DD/MM/YYYY")
      : refurbEstimateDate;

  return (
    <Container sx={sx}>
      <Stack direction="row" mt="32px" justifyContent="space-between">
        <Stack pb="3.2rem">
          <Stack direction="row" alignItems="self-start" spacing={1}>
            {PILLS_CONFIG.map((pillConfig) => {
              const value = vehicleInfo[pillConfig.key];
              const iconSrc = pillConfig.iconKey ? config[pillConfig.iconKey] : null;
              const shouldShow = pillConfig.showKey ? vehicleInfo[pillConfig.showKey] : true;

              return value && shouldShow ? (
                <Stack key={pillConfig.key} alignItems="self-start" mb="2.4rem">
                  {renderPill(pillConfig, value, iconSrc)}
                </Stack>
              ) : null;
            })}
          </Stack>

          <Stack direction="row" spacing={0.5} my="16px">
            <Typography fontSize={["2rem"]} fontWeight={600}>
              {title}
            </Typography>
            <Typography fontSize={["2rem"]} fontWeight={500}>
              {subtitle}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={4} pb="1.2rem" alignItems="center">
            <Stack>
              <Typography
                color={appointmentId === null || appointmentId === "-" ? "#C8C8C8" : "#5B5B5B"}
                fontSize={["1.2rem"]}
                lineHeight="2.4rem"
              >
                App ID
              </Typography>
              <Typography color="#002238" fontSize={["1.4rem"]} fontWeight="600" lineHeight="2.4rem">
                {appointmentId}
              </Typography>
            </Stack>
            <Stack>
              <Typography
                color={stockId === null || stockId === "-" ? "#C8C8C8" : "#5B5B5B"}
                fontSize={["1.2rem"]}
                lineHeight="2.4rem"
              >
                Stock ID
              </Typography>
              <Typography color="#002238" fontSize={["1.4rem"]} fontWeight="600" lineHeight="2.4rem">
                {stockId}
              </Typography>
            </Stack>
            <Stack borderRight="1px solid #EDEDED" pr="3.2rem">
              <Typography
                color={vin === null || vin === "-" ? "#C8C8C8" : "#5B5B5B"}
                fontSize={["1.2rem"]}
                lineHeight="2.4rem"
              >
                VIN
              </Typography>
              <Typography color="#002238" fontSize={["1.4rem"]} fontWeight="600" lineHeight="2.4rem">
                {vin}
              </Typography>
            </Stack>
            {inventoryLocation && (
              <Stack direction="row" spacing={1}>
                <CarFilled htmlColor="#6D6D6D" />
                <Typography fontSize={["1.4rem"]} lineHeight="2.4rem" fontWeight={600}>
                  {inventoryLocation}
                </Typography>
              </Stack>
            )}
            {state && (
              <Stack direction="row" spacing={1}>
                <LocationOnOutlinedIcon htmlColor="#6D6D6D" />
                <Typography fontSize={["1.4rem"]} lineHeight="2.4rem" fontWeight={600}>
                  {state}
                </Typography>
              </Stack>
            )}
            {odometer && (
              <Stack direction="row" spacing={1}>
                <SpeedOutlinedIcon htmlColor="#6D6D6D" />
                <Typography fontSize={["1.4rem"]} lineHeight="2.4rem" fontWeight={600}>
                  {odometer}
                </Typography>
              </Stack>
            )}
            {(!odometer || odometer === "-") && (
              <Stack direction="row" spacing={1}>
                <SpeedOutlinedIcon htmlColor="#6D6D6D" />
                <Typography fontSize={["1.4rem"]} lineHeight="2.4rem" fontWeight={600}>
                  0 kms
                </Typography>
              </Stack>
            )}
          </Stack>

          <Divider flexItem sx={{ borderColor: "#EDEDED", marginBottom: "1.2rem" }} />

          <Stack sx={{ mb: "1.6rem !important" }}>
            <Stack direction="row" spacing={4} alignItems="center">
              {properties &&
                properties.length > 0 &&
                properties.map((item) => {
                  let value = vehicleInfo[item.id];

                  if (vehicleInfo[item.id] && item.id === "recallStatus") {
                    value = vehicleInfo[item.id].title;
                  }
                  const desc = item.desc ? vehicleInfo[item.desc] : item.desc;

                  if (value && desc) {
                    return (
                      <Stack key={item.id}>
                        <Typography
                          color={value === null || value === "-" ? "#C8C8C8" : "#5B5B5B"}
                          fontSize={["1.2rem"]}
                          lineHeight="2.4rem"
                        >
                          {item.name}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Tooltip
                            TransitionComponent={Zoom}
                            title={
                              <Typography color="inherit" variant="caption">
                                {desc}
                              </Typography>
                            }
                            arrow
                          >
                            <ErrorOutlineIcon fontSize="1.3rem" htmlColor={theme.palette.secondary.main} />
                          </Tooltip>
                          {item.id === "publishedStatus" && websiteLink ? (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(websiteLink, "_blank", "noopener,noreferrer");
                              }}
                              sx={{
                                "&:hover": {
                                  bgcolor: "transparent"
                                }
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                color="#0071BC"
                                sx={{ textDecoration: "underline" }}
                              >
                                {value}
                              </Typography>
                            </Button>
                          ) : (
                            <Typography
                              variant="body2"
                              visibility={value === "-" ? "hidden" : "visible"}
                              fontWeight={600}
                            >
                              {value}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    );
                  }

                  return (
                    value && (
                      <Stack key={item.id}>
                        <Typography
                          color={value === null || value === "-" ? "#C8C8C8" : "#5B5B5B"}
                          fontSize={["1.2rem"]}
                          lineHeight="2.4rem"
                        >
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          visibility={value === "-" ? "hidden" : "visible"}
                          fontWeight={600}
                          lineHeight="2.4rem"
                        >
                          {value}
                        </Typography>
                      </Stack>
                    )
                  );
                })}
            </Stack>
            {formattedRefurbEstimateDate && (
              <Stack direction="row" spacing={1} alignItems="flex-start" mt="0.8rem">
                <EventOutlinedIcon htmlColor="#6D6D6D" />
                <Stack>
                  <Typography fontSize={["1.2rem"]} lineHeight="2rem" color="#5B5B5B">
                    Est. refurb completion
                  </Typography>
                  <Typography fontSize={["1.4rem"]} lineHeight="2rem" fontWeight={600}>
                    {formattedRefurbEstimateDate}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
          {infoList?.length > 0 && (
            <Stack spacing={1}>
              {infoList.map((info) => (
                <Stack key={info.key} direction="row" spacing={1}>
                  <Typography fontWeight={600} variant="caption" color="#5B5B5B">
                    {info.title}
                  </Typography>
                  <Typography variant="caption" color="#5B5B5B">
                    {info.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>

        {!hideActions && (
          <Stack alignSelf="stretch" flexDirection="row">
            {ctas.length > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: "#EDEDED" }} />}
            {ctas.length > 0 && (
              <Stack spacing={1} p="0 0 3.2rem 3.2rem">
                <Typography variant="body2" fontWeight={600} pl="1.6rem" pb="0.8rem">
                  Actions
                </Typography>
                <VehicleActionList appointmentId={appointmentId} actions={ctas} leadId={leadId} />
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
